// DIAGNOSTIC-ONLY browser preflight for selected videos. Inspects a file client-side and
// classifies it. It NEVER blocks or changes the upload — purely informational.

const readChunk = (file, start, end) =>
  new Promise((resolve) => {
    const r = new FileReader();
    r.onload = () => resolve(new Uint8Array(r.result));
    r.onerror = () => resolve(new Uint8Array());
    r.readAsArrayBuffer(file.slice(start, end));
  });

// Scan raw bytes for known ISO-BMFF codec fourCCs (works for both .mp4 and .mov / QuickTime).
const scanCodecs = (bytes) => {
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  const has = (t) => s.indexOf(t) !== -1;
  let vcodec = null;
  if (has("hvc1") || has("hev1") || has("hvcC")) vcodec = "HEVC";
  else if (has("avc1") || has("avcC")) vcodec = "H.264";
  else if (has("vp09")) vcodec = "VP9";
  else if (has("av01")) vcodec = "AV1";
  let acodec = null;
  if (has("mp4a")) acodec = "AAC";
  else if (has("Opus") || has("opus")) acodec = "Opus";
  else if (has("ac-3")) acodec = "AC-3";
  return { vcodec, acodec };
};

// Load metadata (duration, dimensions) and best-effort frame rate via requestVideoFrameCallback.
const probeMetadata = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    let done = false;
    const finish = (data) => {
      if (done) return;
      done = true;
      try { v.pause(); } catch (e) {}
      URL.revokeObjectURL(url);
      resolve(data);
    };
    const timeout = setTimeout(() => finish({}), 7000);
    v.muted = true;
    v.preload = "metadata";
    v.src = url;
    v.onloadedmetadata = () => {
      const meta = { duration: v.duration, width: v.videoWidth, height: v.videoHeight };
      // Best-effort fps via requestVideoFrameCallback (only where supported). Bounded to ~1.2s.
      if (typeof v.requestVideoFrameCallback === "function" && v.duration && isFinite(v.duration)) {
        let first = null, frames = 0;
        const step = (now, m) => {
          if (first == null) first = m.mediaTime;
          frames += 1;
          const span = m.mediaTime - first;
          if (frames >= 6 && span > 0) { meta.fps = Math.round(frames / span); clearTimeout(timeout); finish(meta); return; }
          v.requestVideoFrameCallback(step);
        };
        v.requestVideoFrameCallback(step);
        v.play().catch(() => {});
        setTimeout(() => { clearTimeout(timeout); finish(meta); }, 1200);
      } else {
        clearTimeout(timeout);
        finish(meta);
      }
    };
    v.onerror = () => { clearTimeout(timeout); finish({}); };
  });

export const preflightVideo = async (file) => {
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  const container = ext === "mov" ? "MOV" : ext === "mp4" ? "MP4" : ext.toUpperCase();
  const out = { filename: file.name, ext, container, size: file.size };
  try {
    const head = await readChunk(file, 0, Math.min(file.size, 1_500_000));
    const tail = file.size > 1_500_000 ? await readChunk(file, Math.max(0, file.size - 1_000_000), file.size) : new Uint8Array();
    const merged = new Uint8Array(head.length + tail.length);
    merged.set(head, 0); merged.set(tail, head.length);
    Object.assign(out, scanCodecs(merged));
    const meta = await probeMetadata(file);
    Object.assign(out, meta);
  } catch (e) { /* leave whatever we have */ }

  if (out.width && out.height) out.orientation = out.width === out.height ? "square" : out.width > out.height ? "landscape" : "portrait";
  out.hfr = typeof out.fps === "number" && out.fps >= 50;

  // Classify
  if (out.vcodec === "HEVC") { out.status = "hevc"; out.label = "⚠ HEVC/H.265 — requires conversion"; }
  else if (out.vcodec === "H.264" && container === "MOV") { out.status = "h264mov"; out.label = "✓ H.264 MOV — may only need MP4 remux"; }
  else if (out.vcodec === "H.264" && container === "MP4") { out.status = "ready"; out.label = "✓ Ready — H.264 MP4"; }
  else if (out.vcodec) { out.status = "ready"; out.label = `✓ ${out.vcodec} ${container}`; }
  else { out.status = "unknown"; out.label = "? Codec unknown — upload pipeline will inspect"; }
  return out;
};

export const PREFLIGHT_BUCKETS = [
  { key: "ready", label: "Ready" },
  { key: "h264mov", label: "H.264 MOV" },
  { key: "hevc", label: "HEVC / Need Conversion" },
  { key: "hfr", label: "High Frame Rate" },
  { key: "unknown", label: "Codec Unknown" },
];
